import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { PASSWORD_MIN } from "@krut/shared";
import { Badge, Button, Card, ErrorText, Field, Input, PageHeader, Select, Spinner, Switch } from "../components/ui.tsx";
import { useBrand } from "../hooks.ts";

type SsoData = Awaited<ReturnType<typeof load>>;
type Provider = SsoData["providers"][number];
const load = () => unwrap(api.admin.sso.$get());

const names = { google: "Google Workspace", microsoft: "Microsoft Entra ID" } as const;
const setupHelp = {
  google: "Google Cloud Console → APIs & Services → Credentials → OAuth client ID (Web application). Add the callback URL below as an authorised redirect URI.",
  microsoft: "Entra admin center → App registrations → New registration (single tenant). Add the callback URL below as a Web redirect URI, then create a client secret.",
} as const;

export function SsoPage() {
  const brand = useBrand();
  const sso = useQuery({ queryKey: ["sso"], queryFn: load });
  if (sso.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (sso.error) return <ErrorText error={sso.error} />;
  return (
    <>
      <PageHeader title="Sign-in" description={`Choose how people sign in to ${brand.name} and who is allowed in.`} />
      <div className="space-y-4">
        {sso.data.providers.map((p) => <ProviderCard key={p.id} p={p} />)}
        <PasswordCard data={sso.data} />
        <PolicyCard data={sso.data} />
      </div>
    </>
  );
}

function ProviderCard({ p }: { p: Provider }) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(p.enabled);
  const [clientId, setClientId] = useState(p.clientId);
  const [clientSecret, setClientSecret] = useState("");
  const [tenantId, setTenantId] = useState(p.tenantId);
  const [copied, setCopied] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.admin.sso.providers[":id"].$put({
          param: { id: p.id },
          json: { enabled, clientId, clientSecret: clientSecret || undefined, tenantId: p.id === "microsoft" ? tenantId : undefined },
        }),
      ),
    onSuccess: () => {
      setClientSecret("");
      qc.invalidateQueries({ queryKey: ["sso"] });
      qc.invalidateQueries({ queryKey: ["public-config"] });
    },
  });

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{names[p.id]}</h3>
            {p.configured ? <Badge tone={p.enabled ? "good" : "neutral"}>{p.enabled ? "Enabled" : "Disabled"}</Badge> : <Badge>Not configured</Badge>}
          </div>
          <p className="mt-1 max-w-2xl text-sm text-[var(--muted)]">{setupHelp[p.id]}</p>
        </div>
      </div>
      <form className="mt-5 space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Field label="Callback URL">
          <div className="flex gap-2">
            <Input readOnly value={p.callbackUrl} className="font-mono text-xs" />
            <Button type="button" onClick={() => { void navigator.clipboard.writeText(p.callbackUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Client ID"><Input required value={clientId} onChange={(e) => setClientId(e.target.value)} /></Field>
          <Field label="Client secret" hint={p.hasSecret ? "Stored encrypted. Leave blank to keep it." : undefined}>
            <Input type="password" required={!p.hasSecret} value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder={p.hasSecret ? "••••••••" : ""} autoComplete="off" />
          </Field>
          {p.id === "microsoft" && (
            <Field label="Tenant ID" hint="Your organisation's tenant (GUID or company.onmicrosoft.com). Multi-tenant values are refused: they would let outside accounts claim your users' emails.">
              <Input required value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" />
            </Field>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--line)] pt-4">
          <div className="w-60"><Switch label="Allow sign-in" checked={enabled} onChange={setEnabled} /></div>
          <div className="flex items-center gap-3">
            {save.isSuccess && <span className="text-sm text-emerald-700">Saved</span>}
            <Button type="submit" variant="primary" disabled={save.isPending}>Save</Button>
          </div>
        </div>
        <ErrorText error={save.error} />
      </form>
    </Card>
  );
}

function PasswordCard({ data }: { data: SsoData }) {
  const qc = useQueryClient();
  const brand = useBrand();
  const [enabled, setEnabled] = useState(data.passwordSignIn.enabled);
  const save = useMutation({
    mutationFn: () => unwrap(api.admin.sso["password-sign-in"].$put({ json: { enabled } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sso"] });
      qc.invalidateQueries({ queryKey: ["public-config"] });
    },
  });
  return (
    <Card className="p-6">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold">Email and password</h3>
        <Badge tone={data.passwordSignIn.enabled ? "good" : "neutral"}>{data.passwordSignIn.enabled ? "Enabled" : "Off"}</Badge>
      </div>
      <p className="mt-1 max-w-2xl text-sm text-[var(--muted)]">
        For people without a Google or Microsoft work account, such as a reviewer. It doesn't let anyone new in: only people
        already added to {brand.name} can set a password, after entering a code sent to their email. Passwords need at least {PASSWORD_MIN} characters.
        SSO stays the safer choice, since it keeps your identity provider's rules (MFA, offboarding) in charge.
      </p>
      {!data.emailReady && (
        <p className="mt-3 text-sm text-amber-700">Email isn't set up for this install, so nobody could receive a code. Add a Resend key first.</p>
      )}
      <form className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--line)] pt-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="w-72"><Switch label="Allow email and password sign-in" checked={enabled} onChange={setEnabled} disabled={!data.emailReady && !enabled} /></div>
        <div className="flex items-center gap-3">
          {save.isSuccess && <span className="text-sm text-emerald-700">Saved</span>}
          <Button type="submit" variant="primary" disabled={save.isPending || enabled === data.passwordSignIn.enabled}>Save</Button>
        </div>
        <div className="w-full"><ErrorText error={save.error} /></div>
      </form>
    </Card>
  );
}

function PolicyCard({ data }: { data: SsoData }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState(data.signupPolicy.mode);
  const [domains, setDomains] = useState(data.signupPolicy.allowedDomains.join(", "));
  const save = useMutation({
    mutationFn: () =>
      unwrap(api.admin.sso["signup-policy"].$put({ json: { mode, allowedDomains: domains.split(/[\s,]+/).filter(Boolean) } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sso"] }),
  });
  return (
    <Card className="p-6">
      <h3 className="font-semibold">Who can sign in</h3>
      <form className="mt-4 space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Access">
            <Select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
              <option value="invite">Only users an admin has added</option>
              <option value="domain">Anyone from these email domains</option>
            </Select>
          </Field>
          <Field label="Email domains" hint="New users from these domains join the default group.">
            <Input disabled={mode !== "domain"} value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="aeroloop.ai" />
          </Field>
        </div>
        <p className="text-xs text-[var(--muted)]">
          Always admins (set at deploy time): {data.adminEmails.length ? data.adminEmails.join(", ") : "none"}
        </p>
        <ErrorText error={save.error} />
        <div className="flex items-center justify-end gap-3">
          {save.isSuccess && <span className="text-sm text-emerald-700">Saved</span>}
          <Button type="submit" variant="primary" disabled={save.isPending}>Save</Button>
        </div>
      </form>
    </Card>
  );
}
